import { useCallback, useState } from "react";

import { type PropertyGroup } from "./usePropertyFiltering";

export interface SelectedProperty {
    searchPropertyName: string;
    propertyValue: string;
}

export interface UsePropertySelectionResult {
    selectedProperties: Map<string, SelectedProperty>;
    isPropertySelected: (groupName: string, propertyName: string) => boolean;
    getGroupStats: (groupName: string) => {
        total: number;
        selected: number;
        isEmpty: boolean;
        isFullySelected: boolean;
        isPartiallySelected: boolean;
    };
    togglePropertySelection: (groupName: string, propertyName: string, propertyValue: string) => void;
    toggleGroupSelection: (groupName: string) => void;
    clearSelection: () => void;
}

/**
 * Hook for managing property selection state.
 * Handles selection logic only - no data processing or filtering.
 */
export function usePropertySelection(groupedProperties: PropertyGroup[]): UsePropertySelectionResult {
    const [selectedProperties, setSelectedProperties] = useState<Map<string, SelectedProperty>>(new Map());

    const createPropertyKey = (groupName: string, propertyName: string) => {
        return groupName ? `${groupName}/${propertyName}` : propertyName;
    };

    const createSelectedProperty = (
        groupName: string,
        propertyName: string,
        propertyValue: string,
    ): SelectedProperty => {
        const isGeneralProperty = groupName === "General" || groupName === "general" || !groupName;
        const searchPropertyName = isGeneralProperty ? propertyName : `${groupName}/${propertyName}`;

        return {
            searchPropertyName,
            propertyValue,
        };
    };

    const getGroupProperties = (groupName: string): [string, string][] => {
        const group = groupedProperties.find((g) => g.name === groupName);
        return group?.properties || [];
    };

    const isPropertySelected = (groupName: string, propertyName: string) => {
        const key = createPropertyKey(groupName, propertyName);
        return selectedProperties.has(key);
    };

    const getGroupStats = (groupName: string) => {
        const groupProps = getGroupProperties(groupName);
        const selectedCount = groupProps.filter(([propName]) => isPropertySelected(groupName, propName)).length;

        return {
            total: groupProps.length,
            selected: selectedCount,
            isEmpty: selectedCount === 0,
            isFullySelected: selectedCount > 0 && selectedCount === groupProps.length,
            isPartiallySelected: selectedCount > 0 && selectedCount < groupProps.length,
        };
    };

    const togglePropertySelection = (groupName: string, propertyName: string, propertyValue: string) => {
        const key = createPropertyKey(groupName, propertyName);
        setSelectedProperties((prev) => {
            const newMap = new Map(prev);
            if (newMap.has(key)) {
                newMap.delete(key);
            } else {
                newMap.set(key, createSelectedProperty(groupName, propertyName, propertyValue));
            }
            return newMap;
        });
    };

    const toggleGroupSelection = (groupName: string) => {
        const groupProps = getGroupProperties(groupName);
        const { isFullySelected } = getGroupStats(groupName);

        setSelectedProperties((prev) => {
            const newMap = new Map(prev);
            groupProps.forEach(([propName, propValue]) => {
                const key = createPropertyKey(groupName, propName);
                if (isFullySelected) {
                    newMap.delete(key);
                } else {
                    newMap.set(key, createSelectedProperty(groupName, propName, propValue));
                }
            });
            return newMap;
        });
    };

    const clearSelection = useCallback(() => setSelectedProperties(new Map()), []);

    return {
        selectedProperties,
        isPropertySelected,
        getGroupStats,
        togglePropertySelection,
        toggleGroupSelection,
        clearSelection,
    };
}
